import React, {createContext, useContext, useState} from "react";
import {NewClassroom, NewSchool, NewUser} from "../types";
import {getDefaultSession} from "../utils/sessionTermOptions";

interface SignUpContextType {
    currentTab: number;
    noOfTabs: number;
    nextTab: () => void;
    previousTab: () => void;
    newUser: Partial<NewUser>;
    updateNewUser: (details: Partial<NewUser>) => void;
    newSchool: Partial<NewSchool>;
    updateNewSchool: (details: Partial<NewSchool>) => void;
    classrooms_: NewClassroom[];
    setClassrooms_: (classrooms: NewClassroom[]) => void;
}

const DEFAULT_CLASSROOMS: NewClassroom[] = [{ name: "My Classroom", children: [] }];

export const SignUpContext = createContext<SignUpContextType | null>(null);

const DEFAULT_SCHOOL: Partial<NewSchool> = {
    name: "",
    sessionName: getDefaultSession(),
    termName: "",
};

export const SignUpContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [currentTab, setCurrentTab] = useState<number>(1);
    const [newUser, setNewUser] = useState<Partial<NewUser>>({});
    const [newSchool, setNewSchool] = useState<Partial<NewSchool>>({...DEFAULT_SCHOOL});
    const [classrooms_, setClassrooms_] = useState<NewClassroom[]>([...DEFAULT_CLASSROOMS]);

    // Homeschool path adds a Children step (4 steps total). All other paths use 3.
    const noOfTabs = newUser.accountTypeChoice === 'homeschool' ? 4 : 3;

    const updateNewUser = (details: Partial<NewUser>) => {
        setNewUser(previousState => ({ ...previousState, ...details }));
    };

    const updateNewSchool = (details: Partial<NewSchool>) => {
        setNewSchool(previousState => ({ ...previousState, ...details }));
    };

    const selectTab = (pageNumber: number) => {
        if (pageNumber === 1) {
            setNewUser({});
            setNewSchool({...DEFAULT_SCHOOL});
            setClassrooms_([{ name: "My Classroom", children: [] }]);
        }

        // Clear previously entered passwords only when navigating back to an
        // earlier step (so the user re-enters them). Advancing from step 3 to
        // step 4 in the homeschool flow must keep them so the final submit works.
        if (pageNumber < 3) {
            updateNewUser({
                password: undefined,
                passwordConfirmation: undefined
            });
        }

        setCurrentTab(pageNumber);
    };

    const nextTab = () => {
        const nextTabNumber = currentTab + 1;
        if (nextTabNumber > noOfTabs || nextTabNumber < 1) {
            return;
        }

        selectTab(nextTabNumber);
        window.scrollTo(0, 0);
    };

    const previousTab = () => {
        const nextTabNumber = currentTab - 1;
        if (nextTabNumber > noOfTabs || nextTabNumber < 1) {
            return;
        }

        selectTab(nextTabNumber);
    };

    const value = {
        currentTab, noOfTabs, nextTab, previousTab,
        newUser, updateNewUser,
        newSchool, updateNewSchool,
        classrooms_, setClassrooms_,
    };

    return (
        <SignUpContext.Provider value={value}> {children} </SignUpContext.Provider>
    );
};

export default SignUpContext;

export const useSignUp = (): SignUpContextType => {
    const ctx = useContext(SignUpContext);
    if (!ctx) throw new Error('useSignUp must be used within SignUpContextProvider');
    return ctx;
};
